import type { Ref } from "vue";
import { onBeforeUnmount, ref, watch } from "vue";

const CONVERTED_TYPE = "image/jpeg";
const CONVERTED_QUALITY = 0.92;

export interface UseHeicObjectUrlResult {
  objectUrl: Ref<string | undefined>;
  isConverting: Ref<boolean>;
  hasFailed: Ref<boolean>;
}

// No browser decodes HEIC in an <img> except Safari, so the bytes are converted to
// a JPEG blob the ordinary image path can render.
export const useHeicObjectUrl = (
  sourceUrl: Ref<string | undefined>,
  enabled?: Ref<boolean>,
): UseHeicObjectUrlResult => {
  const isEnabled = (): boolean => enabled?.value ?? true;

  const objectUrl = ref<string | undefined>(undefined);
  const isConverting = ref<boolean>(false);
  const hasFailed = ref<boolean>(false);

  let conversionGeneration = 0;

  const release = (): void => {
    if (objectUrl.value) {
      URL.revokeObjectURL(objectUrl.value);
      objectUrl.value = undefined;
    }
  };

  const convert = async (): Promise<void> => {
    const generation = ++conversionGeneration;
    release();
    hasFailed.value = false;

    if (!isEnabled() || !sourceUrl.value) {
      isConverting.value = false;
      return;
    }

    isConverting.value = true;
    try {
      const response = await fetch(sourceUrl.value);
      if (!response.ok) {
        throw new Error(`HEIC fetch failed with status ${response.status}`);
      }
      const blob = await response.blob();

      const { heicTo } = await import("heic-to");
      const converted = await heicTo({ blob, type: CONVERTED_TYPE, quality: CONVERTED_QUALITY });
      if (generation !== conversionGeneration) {
        return;
      }
      objectUrl.value = URL.createObjectURL(converted);
    } catch {
      if (generation === conversionGeneration) {
        hasFailed.value = true;
      }
    } finally {
      if (generation === conversionGeneration) {
        isConverting.value = false;
      }
    }
  };

  watch([sourceUrl, isEnabled], convert, { immediate: true });

  onBeforeUnmount(() => {
    conversionGeneration++;
    release();
  });

  return { objectUrl, isConverting, hasFailed };
};
